import { format, startOfMonth } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { agent } from 'services/service';
import {
  buildEmployeeDetailPath,
  getLastMonthPeriod,
  parsePeriodFromSearchParams,
  periodToSearchParams,
  type AttendanceDashboardPeriod
} from 'utils/attendanceDashboardPeriod';
import {
  AttendanceCalendarCore,
  getDefaultActiveMonth
} from './AttendanceCalendarCore';
import { useAttendanceCalendar } from './useAttendanceCalendar';

export const EmployeeAttendanceCalendar = () => {
  const navigate = useNavigate();
  const { epfNumber = '' } = useParams<{ epfNumber: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<AttendanceDashboardPeriod>(() =>
    parsePeriodFromSearchParams(searchParams, getLastMonthPeriod())
  );
  const [activeMonth, setActiveMonth] = useState(() =>
    getDefaultActiveMonth(
      parsePeriodFromSearchParams(searchParams, getLastMonthPeriod()).from
    )
  );
  const [exporting, setExporting] = useState(false);

  const {
    recordsByDate,
    employeeName,
    minimumOfficeDays,
    legendItems,
    statusLabels,
    loading,
    error
  } = useAttendanceCalendar(epfNumber, period.from, period.to);

  useEffect(() => {
    const nextPeriod = parsePeriodFromSearchParams(
      searchParams,
      getLastMonthPeriod()
    );
    setPeriod(nextPeriod);
    setActiveMonth(startOfMonth(nextPeriod.from));
  }, [epfNumber, searchParams]);

  const handlePeriodChange = useCallback(
    (nextPeriod: AttendanceDashboardPeriod) => {
      setPeriod(nextPeriod);
      setActiveMonth(startOfMonth(nextPeriod.from));
      setSearchParams(periodToSearchParams(nextPeriod), { replace: true });
    },
    [setSearchParams]
  );

  const displayName = employeeName || epfNumber;

  const handleExportMonth = async () => {
    if (exporting) return;

    setExporting(true);
    try {
      await agent.AttendanceTrackingService.exportAttendanceExcel({
        from: format(period.from, 'yyyy-MM-dd'),
        to: format(period.to, 'yyyy-MM-dd'),
        employeeIds: [epfNumber]
      });
      toast.success('Attendance export completed.');
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to export attendance records.';
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <AttendanceCalendarCore
      variant="admin"
      epfNumber={epfNumber}
      employeeName={displayName}
      recordsByDate={recordsByDate}
      legendItems={legendItems}
      statusLabels={statusLabels}
      loading={loading}
      error={error}
      viewMode="range"
      showViewModeToggle={false}
      rangeStart={period.from}
      rangeEnd={period.to}
      period={period}
      onPeriodChange={handlePeriodChange}
      activeMonth={activeMonth}
      onActiveMonthChange={setActiveMonth}
      minimumOfficeDays={minimumOfficeDays}
      crumbs={[
        { label: 'Attendance Tracking', to: '/attendance-tracking' },
        {
          label: 'Employee Attendance',
          to: '/attendance-tracking/employees'
        },
        {
          label: displayName,
          to: buildEmployeeDetailPath(epfNumber, period)
        },
        { label: format(activeMonth, 'MMMM yyyy') }
      ]}
      onBack={() => navigate(buildEmployeeDetailPath(epfNumber, period))}
      backLabel="Back to Employee"
      onExport={handleExportMonth}
    />
  );
};
